import { useMemo } from "react"
import type { HTMLAttributes, Key } from "react"
import { omit } from "lodash"
import { Autocomplete, TextField } from "@mui/material"
import type { TextFieldProps } from "@mui/material"
import type { PopperProps } from "@mui/material/Popper"
import classnames from "classnames"
import Tooltip from "components/material/tooltip"
import { ModelSelectorOption } from "./model-selector-option"
import type { ModelSelectorListOption } from "./types"
import "./style.scss"

export type ModelSelectorTextFieldProps = Omit<Partial<TextFieldProps>, "InputProps" | "inputProps">

export interface ModelSelectorAutocompleteProps {
  models: ModelSelectorListOption[]
  value: string
  onChange: (modelId: string) => void
  disabled?: boolean
  className?: string
  fullWidth?: boolean
  label?: string
  inputId?: string
  textFieldProps?: ModelSelectorTextFieldProps
  loading?: boolean
  requiresResourceCoverage?: boolean
  resourceTypeLabel?: string
  disableUnderline?: boolean
  disablePortal?: boolean
  displayNameFallback?: string
  listClassName?: string
  popperPlacement?: PopperProps["placement"]
}

function isModelRow(option: ModelSelectorListOption) {
  return option.rowType == null || option.rowType === "model"
}

/** MUI passes `key` on the props object for `renderOption`; spread the rest onto the row. */
type AutocompleteListOptionProps = HTMLAttributes<HTMLLIElement> & { key?: Key }

function getModelSelectorListOption(
  rawProps: AutocompleteListOptionProps,
  option: ModelSelectorListOption,
  coverageTooltip: string,
  requiresResourceCoverage: boolean,
) {
  const props = omit(rawProps, "key")
  const key = option.id
  if (option.rowType === "email") {
    return (
      <li
        key={key}
        {...props}
        className={classnames(props.className, "model-selector-email-row")}
      >
        Want access to more models?&nbsp;
        <span className="more-model-link">Email us</span>
      </li>
    )
  }

  const coverageDisabled = isModelRow(option) && requiresResourceCoverage && option.hasCoverage === false

  if (coverageDisabled) {
    return (
      <Tooltip
        key={key}
        title={coverageTooltip}
        classes={{ popper: "model-selector-coverage-info", tooltip: "coverage-insufficient-tooltip" }}
      >
        <div>
          <ModelSelectorOption option={option} {...props} disabled />
        </div>
      </Tooltip>
    )
  }
  const optionalItemClassName = isModelRow(option) && option.rowType !== "additional" ? "model-selector-item" : undefined

  return (
    <ModelSelectorOption
      key={key}
      {...props}
      option={option}
      data-test-class={optionalItemClassName}
      className={classnames(props.className, optionalItemClassName)}
    />
  )
}

export default function ModelSelectorAutocomplete({
  models,
  value,
  onChange,
  disabled = false,
  className = "model-selector",
  fullWidth = false,
  label,
  inputId = "model-selector-display",
  textFieldProps,
  loading = false,
  requiresResourceCoverage = false,
  resourceTypeLabel = "resource",
  disableUnderline = false,
  disablePortal = false,
  displayNameFallback,
  listClassName = "model-selector-list",
  popperPlacement,
}: ModelSelectorAutocompleteProps) {
  const selectedModel = useMemo(() => {
    const found = models.find(({ id }) => id === value)
    if (found) {
      return found
    }
    if (displayNameFallback && value) {
      return { id: value, name: displayNameFallback, rowType: "model" as const }
    }
    return null
  }, [models, value, displayNameFallback])

  const coverageTooltip = `Model coverage for this ${resourceTypeLabel} is insufficient.`

  return (
    <Autocomplete
      className={className}
      fullWidth={fullWidth}
      disablePortal={disablePortal}
      data-test-id="model-selector"
      options={models}
      value={selectedModel ?? undefined}
      loading={loading}
      onChange={(_, newValue) => {
        if (newValue) {
          onChange(newValue.id)
        }
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, item) => option.id === item.id}
      getOptionDisabled={(option) => isModelRow(option) && requiresResourceCoverage && option.hasCoverage === false}
      disabled={disabled}
      disableClearable
      blurOnSelect
      renderOption={(props, option) => getModelSelectorListOption(props, option, coverageTooltip, requiresResourceCoverage)}
      slotProps={{
        paper: {
          className: "model-selector-autocomplete-paper",
        },
        popper: {
          className: "model-selector-autocomplete-popper",
          ...(popperPlacement != null ? { placement: popperPlacement } : {}),
        },
        listbox: {
          className: listClassName,
        },
      }}
      renderInput={(params) => {
        const labelLength = selectedModel?.name?.length ?? 0
        const htmlSize = Math.max(10, Math.min(72, labelLength + 2))
        return (
          <TextField
            {...params}
            {...(textFieldProps ?? {})}
            fullWidth={false}
            variant="standard"
            {...(label != null ? { label } : {})}
            inputProps={{
              ...params.inputProps,
              size: htmlSize,
              ...(inputId ? { id: inputId } : {}),
            }}
            InputProps={{
              ...params.InputProps,
              disableUnderline,
            }}
          />
        )
      }}
    />
  )
}
